import React, { useState } from 'react';

import { cn, ia } from 'lib/helpers/utility';
import useTimezone from 'lib/hooks/useTimezone';

import Icon from '../../Icon/Icon';
import Input from '../../Input/Input';
import DatePicker from '../../DatePicker/DatePicker';
import Typography from '../../Typography/Typography';
import { getOptionsByDirection, getSuggestions } from '../lib/helpers';
import { anchorRangeToZone, toCalendarDay, toCalendarDayRange } from '../lib/dayBoundaries';

const Option = ({ label, optionId, onClick, selectedOption }) => (
  <button
    type="button"
    className="flex h-8 w-full items-center justify-between px-4 hover:bg-primary-50"
    onClick={onClick}>
    <Typography className="cursor-pointer whitespace-nowrap" size="small">
      {label}
    </Typography>
    {selectedOption?.id === optionId && <Icon icon="check-linear" color="neutral" shadow={700} />}
  </button>
);

const DateRangeContent = ({
  onChange,
  value,
  onClose = () => {},
  className,
  dataKey,
  formula,
  data,
  direction: directionProp,
  dateOnly: dateOnlyProp
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [input, setInput] = useState('');
  const timezone = useTimezone();
  const direction = directionProp || data?.direction || 'past';
  // Rendered two ways: with explicit props from DateRangeSelector (the applied chip), and with the whole
  // filter definition as `data` from Filters.jsx (the filter popover's formula submenu). Read both, the
  // same way `direction` above does, or the popover path silently loses the flag.
  const dateOnly = !!(dateOnlyProp || data?.dateOnly);

  const handleChange = (event) => {
    const val = event.target.value;
    setInput(val);

    const number = parseInt(val, 10);
    if (!isNaN(number) && number >= 0) {
      setSuggestions(getSuggestions(direction, number, timezone));
    } else {
      setSuggestions([]);
    }
  };

  const onSelectOption = (option) => {
    const optionId = option.id || option.label;
    setInput('');
    setSuggestions([]);
    if (!option.value) {
      onChange(
        { id: optionId, startDate: null, endDate: null, timezone, label: option.label },
        dataKey
      );
      onClose();
      return;
    }
    const optionValue = dateOnly
      ? {
          startDate: toCalendarDay(option.value.startDate, timezone),
          endDate: toCalendarDay(option.value.endDate, timezone)
        }
      : { ...option.value, timezone };

    onChange({ id: optionId, ...optionValue, label: option.label }, dataKey, formula);
    onClose();
  };

  const onRangeChange = (ranges) => {
    if (!ranges?.[0] || !ranges?.[1]) return;
    const [startDate, endDate] = ranges;
    // A date-only filter targets a DATE column, so the picker hands back calendar days and there is no
    // zone to stamp. Otherwise the picker already resolved the day boundaries in `timezone` (00:00 and
    // 23:59 there); re-anchoring them here would read each instant's *browser-local* day first, which
    // pushes an end-of-day boundary into the next day for any zone west of the browser.
    onChange(
      dateOnly
        ? {
            id: 'custom-date-range',
            startDate: toCalendarDay(startDate, timezone),
            endDate: toCalendarDay(endDate, timezone)
          }
        : { id: 'custom-date-range', startDate, endDate, timezone },
      dataKey,
      formula
    );
    onClose();
  };

  // Anchored so the picker opens showing the same boundaries it would emit, with real times. A
  // date-only range is already calendar days and must not be turned back into instants.
  const anchored = dateOnly
    ? toCalendarDayRange(value, timezone)
    : anchorRangeToZone(value, timezone);
  const pickerValue =
    anchored?.startDate && anchored?.endDate ? [anchored.startDate, anchored.endDate] : null;

  const defaultOptions = getOptionsByDirection(direction, timezone);
  const toRenderOptions = ia(suggestions)
    ? suggestions
    : defaultOptions.filter((o) => o.id !== 'custom-date-range');

  return (
    <div className={cn('flex flex-col gap-y-2 pb-2', className)}>
      <Input
        inputWrapperClassName="w-full border-0 border-b border-neutral-200 rounded-none"
        placeholder="e.g. 2d, 4d, 8w"
        value={input}
        onChange={handleChange}
      />
      {toRenderOptions.map((option) => (
        <Option
          key={option.id || option.label}
          label={option.label}
          optionId={option.id || option.label}
          onClick={() => onSelectOption(option)}
          selectedOption={value}
        />
      ))}
      <DatePicker
        isDateRange
        isDateOnly={dateOnly}
        isTimeRange={!dateOnly}
        isTimezone={!dateOnly}
        showTimeRightComponent={false}
        panelClassName="z-[102]"
        value={pickerValue}
        onChange={onRangeChange}
        customButtonTrigger={
          <div className="flex h-8 cursor-pointer items-center justify-between px-4 hover:bg-primary-50">
            <Typography className="cursor-pointer whitespace-nowrap" size="small">
              Custom range
            </Typography>
          </div>
        }
      />
    </div>
  );
};

export default DateRangeContent;
